import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';

import {
  getPreflightCheckTitleLink,
  getPreflightFixLinkKey,
  getPreflightFixPathLabel,
  preflightStatusConfig,
} from '../preflight-check.helpers';
import type { PreflightCheckItemProps } from '../preflight-check.types';

import { cn } from '~shared/shadcn/lib/utils';

export const PreflightCheckItem = ({ check }: PreflightCheckItemProps) => {
  const { Icon, iconClassName, rowClassName } = preflightStatusConfig[check.status];
  const titleLink = getPreflightCheckTitleLink(check);

  return (
    <div
      className={cn(
        'flex gap-3 rounded-lg border p-4',
        rowClassName,
      )}
    >
      <Icon
        className={cn('mt-0.5 size-5 shrink-0', iconClassName)}
        aria-hidden
      />
      <div className='min-w-0 space-y-2'>
        {titleLink ? (
          <Link
            to={titleLink}
            className='inline-flex items-center gap-1 text-sm font-semibold text-foreground underline underline-offset-2'
          >
            {check.label}
            <ExternalLink className='size-3.5 shrink-0' aria-hidden />
          </Link>
        ) : (
          <p className='text-sm font-semibold text-foreground'>{check.label}</p>
        )}
        <p className='text-sm text-muted-foreground'>{check.message}</p>
        {check.values?.length ? (
          <dl className='grid gap-1 rounded-md border border-black/5 bg-white/70 px-3 py-2 text-sm'>
            {check.values.map((entry) => (
              <div
                key={`${check.id}-${entry.label}`}
                className='grid grid-cols-[minmax(0,9rem)_1fr] gap-x-3 gap-y-0.5'
              >
                <dt className='font-medium text-muted-foreground'>
                  {entry.label}
                </dt>
                <dd className='whitespace-pre-wrap break-words text-foreground'>
                  {entry.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        {check.fixLinks?.length ? (
          <div className='space-y-1'>
            {check.fixLinksTotal &&
            check.fixLinksTotal > check.fixLinks.length ? (
              <p className='text-sm text-muted-foreground'>
                Showing {check.fixLinks.length} of {check.fixLinksTotal} items.
              </p>
            ) : null}
            <ul className='space-y-1'>
              {check.fixLinks.map((link) => (
                <li key={getPreflightFixLinkKey(check.id, link)}>
                  <Link
                    to={link.to}
                    params={link.params}
                    className='inline-flex items-center gap-1 text-sm font-medium text-foreground underline underline-offset-2'
                  >
                    {link.label}
                    <ExternalLink className='size-3.5 shrink-0' aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
            {check.fixPath &&
            check.fixLinksTotal &&
            check.fixLinksTotal > check.fixLinks.length ? (
              <Link
                to={check.fixPath}
                className='inline-flex items-center gap-1 text-sm font-medium text-foreground underline underline-offset-2'
              >
                {getPreflightFixPathLabel(
                  check.fixPath,
                  check.status,
                  check.id,
                )}
                <ExternalLink className='size-3.5 shrink-0' aria-hidden />
              </Link>
            ) : null}
          </div>
        ) : check.fixPath && !titleLink ? (
          <Link
            to={check.fixPath}
            className='inline-flex items-center gap-1 text-sm font-medium text-foreground underline underline-offset-2'
          >
            {getPreflightFixPathLabel(check.fixPath, check.status, check.id)}
            <ExternalLink className='size-3.5 shrink-0' aria-hidden />
          </Link>
        ) : null}
      </div>
    </div>
  );
};
